import { useEffect, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useWidget } from '@/state/WidgetContext';
import BotMessage from '@/components/ChatMessage/BotMessage';
import ProgressIndicator from '@/components/ProgressIndicator/ProgressIndicator';
import OptionButton from '@/components/OptionButton/OptionButton';
import LiveChatScreen from '@/screens/LiveChatScreen';
import { liveChatService } from '@/services/liveChat';
import { SCREENS, MANAGER_TIMEOUT_MS } from '@/utils/constants';
import styles from '@/styles/chat.module.scss';

const PersonIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
  </svg>
);

export default function ManagerScreen() {
  const { t } = useLocalization();
  const { goTo, state } = useWidget();
  const [activeDot, setActiveDot] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [hasConnectError, setHasConnectError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const dotTimer = setInterval(() => {
      setActiveDot(prev => (prev + 1) % 3);
    }, 500);

    const offlineTimer = setTimeout(() => {
      clearInterval(dotTimer);
      goTo(SCREENS.OFFLINE);
    }, MANAGER_TIMEOUT_MS);

    setHasConnectError(false);
    liveChatService
      .connect(state.user)
      .then(() => {
        if (!isMounted) return;
        clearInterval(dotTimer);
        clearTimeout(offlineTimer);
        setIsConnected(true);
      })
      .catch((error) => {
        if (!isMounted) return;
        clearInterval(dotTimer);
        clearTimeout(offlineTimer);
        setHasConnectError(true);
        console.error('[LiveChat] Failed to connect manager chat:', error);
      });

    return () => {
      isMounted = false;
      clearInterval(dotTimer);
      clearTimeout(offlineTimer);
    };
  }, [goTo, state.user, retryKey]);

  if (isConnected) {
    return <LiveChatScreen />;
  }

  if (hasConnectError) {
    return (
      <div className={styles.messages}>
        <div className={styles.offline}>
          <div className={styles.managerErrorHero}>
            <ErrorIcon />
          </div>
          <p className={styles.offlineMessage}>{t('manager.connectionError')}</p>
          <div className={styles.managerErrorAction}>
            <OptionButton
              variant="primary"
              onClick={() => {
                setIsConnected(false);
                setHasConnectError(false);
                setRetryKey(prev => prev + 1);
              }}
            >
              {t('manager.retry')}
            </OptionButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.messages}>
      <BotMessage text={t('manager.connecting')}>
        <div className={styles.managerAnim}>
          <div className={styles.managerAvatar}>
            <PersonIcon />
          </div>
          <ProgressIndicator activeIndex={activeDot} />
        </div>
      </BotMessage>
    </div>
  );
}
